<template>
  <div id="app" :class="{ 'is-login-page': isLoginPage }">
    <!-- 登录页：全屏，无侧栏 -->
    <template v-if="isLoginPage">
      <router-view />
    </template>

    <!-- 业务页：左侧导航 + 右侧内容 -->
    <template v-else>
      <div class="layout">
        <!-- 移动端遮罩 -->
        <div
          class="sidebar-overlay"
          v-if="sidebarOpen"
          @click="sidebarOpen = false"
        ></div>

        <aside class="sidebar" :class="{ open: sidebarOpen, collapsed: sidebarCollapsed }">
          <div class="sidebar-logo">
            <img src="@/assets/logo.png" alt="订单管理系统" class="logo-img">
            <router-link
              v-show="!sidebarCollapsed"
              to="/"
              class="brand-link"
              @click.native="closeSidebarOnMobile"
            >
              订单管理系统
            </router-link>
          </div>

          <nav class="sidebar-menu" v-if="isLoggedIn">
            <router-link
              to="/"
              class="nav-link"
              exact
              @click.native="closeSidebarOnMobile"
            >
              <i class="el-icon-s-home"></i>
              <span v-show="!sidebarCollapsed">首页</span>
            </router-link>
            <router-link
              v-if="isAdmin"
              to="/product-management"
              class="nav-link"
              @click.native="closeSidebarOnMobile"
            >
              <i class="el-icon-goods"></i>
              <span v-show="!sidebarCollapsed">产品管理</span>
            </router-link>
            <router-link
              v-if="isAdmin"
              to="/agent-accounts"
              class="nav-link"
              @click.native="closeSidebarOnMobile"
            >
              <i class="el-icon-user"></i>
              <span v-show="!sidebarCollapsed">招商账号</span>
            </router-link>
            <router-link
              to="/order-form"
              class="nav-link"
              @click.native="closeSidebarOnMobile"
            >
              <i class="el-icon-document-add"></i>
              <span v-show="!sidebarCollapsed">创建订单</span>
            </router-link>
            <router-link
              v-if="isAdmin"
              to="/admin-dashboard"
              class="nav-link"
              @click.native="closeSidebarOnMobile"
            >
              <i class="el-icon-s-check"></i>
              <span v-show="!sidebarCollapsed">订单管理</span>
            </router-link>
            <router-link
              v-if="isAdmin"
              to="/config-management"
              class="nav-link"
              @click.native="closeSidebarOnMobile"
            >
              <i class="el-icon-setting"></i>
              <span v-show="!sidebarCollapsed">系统配置管理</span>
            </router-link>
            
            <router-link
              v-if="!isAdmin"
              to="/history-orders"
              class="nav-link"
              @click.native="closeSidebarOnMobile"
            >
              <i class="el-icon-time"></i>
              <span v-show="!sidebarCollapsed">历史订单</span>
            </router-link>
          </nav>
        </aside>

        <div class="layout-main">
          <header class="navbar">
            <div class="navbar-left">
              <button
                type="button"
                class="hamburger"
                @click="toggleSidebar"
                aria-label="切换导航"
              >
                <i class="el-icon-s-fold" v-if="!sidebarCollapsed || sidebarOpen"></i>
                <i class="el-icon-s-unfold" v-else></i>
              </button>
              <el-breadcrumb separator="/">
                <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
                <el-breadcrumb-item v-if="$route.path !== '/'">{{ pageTitle }}</el-breadcrumb-item>
              </el-breadcrumb>
            </div>

            <div class="navbar-right" v-if="isLoggedIn">
              <el-dropdown
                @command="handleUserMenuCommand"
                trigger="click"
                placement="bottom-end"
              >
                <span class="user-dropdown-trigger">
                  <span class="user-avatar">{{ userInitial }}</span>
                  <span class="user-name" v-if="userInfo">{{ userInfo.username }}</span>
                  <i class="el-icon-caret-bottom"></i>
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="agentAccounts" v-if="isAdmin">
                    <i class="el-icon-user"></i> 招商账号
                  </el-dropdown-item>
                  <el-dropdown-item command="changePassword">
                    <i class="el-icon-edit"></i> 修改密码
                  </el-dropdown-item>
                  <el-dropdown-item divided command="logout">
                    <i class="el-icon-switch-button"></i> 退出登录
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </header>

          <div class="tags-view" v-if="isLoggedIn">
            <span class="tags-item active">{{ pageTitle }}</span>
          </div>

          <main class="main-content">
            <router-view />
          </main>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

const PAGE_TITLES = {
  '/': '首页',
  '/order-form': '创建订单',
  '/history-orders': '历史订单',
  '/admin-dashboard': '订单管理',
  '/config-management': '系统配置管理',
  '/product-management': '产品管理',
  '/agent-accounts': '招商账号',
  '/account-management': '修改密码'
}

export default {
  name: 'App',
  data() {
    return {
      sidebarOpen: false,
      sidebarCollapsed: false
    }
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'user/isLoggedIn',
      userInfo: 'user/userInfo',
      permission: 'user/permission'
    }),
    isLoginPage() {
      return this.$route.path === '/login'
    },
    isAdmin() {
      try {
        const localStoragePermission = localStorage.getItem('user-permission')
        const isAdminFromLocalStorage = localStoragePermission === '1'
        const isAdminFromVuex = this.permission === '1' || this.permission === 1
        return isAdminFromLocalStorage || isAdminFromVuex
      } catch (error) {
        return false
      }
    },
    pageTitle() {
      return PAGE_TITLES[this.$route.path] || '订单管理系统'
    },
    userInitial() {
      const name = (this.userInfo && this.userInfo.username) || '?'
      return name.charAt(0).toUpperCase()
    }
  },
  watch: {
    $route() {
      this.sidebarOpen = false
    }
  },
  methods: {
    ...mapActions({
      logout: 'user/logout'
    }),
    closeSidebarOnMobile() {
      if (window.innerWidth <= 768) {
        this.sidebarOpen = false
      }
    },
    toggleSidebar() {
      if (window.innerWidth <= 768) {
        this.sidebarOpen = !this.sidebarOpen
      } else {
        this.sidebarCollapsed = !this.sidebarCollapsed
      }
    },
    handleUserMenuCommand(command) {
      if (command === 'agentAccounts') {
        this.$router.push('/agent-accounts')
      } else if (command === 'changePassword') {
        this.$router.push('/account-management')
      } else if (command === 'logout') {
        this.handleLogout()
      }
    },
    handleLogout() {
      if (confirm('确定要退出登录吗？')) {
        this.logout().then(() => {
          this.$router.push('/login')
        })
      }
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600&display=swap');

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Inter', "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #F7F5F1;
  color: #1E2227;
}

#app {
  min-height: 100vh;
}

#app.is-login-page {
  display: block;
}

.layout {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  width: 200px;
  flex-shrink: 0;
  background: #1E2227;
  color: #FFFFFF;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 1001;
  transition: width 0.28s;
  overflow: hidden;
  border-right: 1px solid #E7E3DA;
}

.sidebar.collapsed {
  width: 54px;
}

.sidebar-logo {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  background: #1E2227;
  overflow: hidden;
  gap: 12px;
  border-bottom: 1px solid rgba(231, 227, 218, 0.1);
}

.logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 4px;
}

.brand-link {
  color: #FFFFFF;
  text-decoration: none;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  font-family: 'Inter', sans-serif;
}

.sidebar-menu {
  flex: 1;
  padding: 12px 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  padding: 0 20px;
  height: 44px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
  font-family: 'Inter', sans-serif;
}

.sidebar.collapsed .nav-link {
  padding: 0;
  justify-content: center;
}

.nav-link i {
  font-size: 18px;
  width: 20px;
  text-align: center;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #FFFFFF;
}

.nav-link.router-link-active {
  background: #3C5A78 !important;
  color: #FFFFFF !important;
}

.layout-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #F7F5F1;
}

.navbar {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #FFFFFF;
  border-bottom: 1px solid #E7E3DA;
  position: sticky;
  top: 0;
  z-index: 50;
}

.navbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hamburger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6B7077;
  font-size: 20px;
  border-radius: 6px;
  transition: all 0.2s;
}

.hamburger:hover {
  background: #F7F5F1;
  color: #1E2227;
}

.navbar-right {
  display: flex;
  align-items: center;
}

.user-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 6px;
  color: #1E2227;
  transition: all 0.2s;
}

.user-dropdown-trigger:hover {
  background: #F7F5F1;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #3C5A78;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #1E2227;
  font-family: 'Inter', sans-serif;
}

.tags-view {
  background: #FFFFFF;
  border-bottom: 1px solid #E7E3DA;
  padding: 10px 20px;
}

.tags-item {
  display: inline-block;
  height: 28px;
  line-height: 26px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid #3C5A78;
  border-radius: 4px;
  color: #FFFFFF;
  background: #3C5A78;
  font-family: 'Inter', sans-serif;
}

.tags-item.active {
  background: #3C5A78;
  color: #FFFFFF;
  border-color: #3C5A78;
}

.main-content {
  flex: 1;
  padding: 20px;
  overflow: auto;
}

.sidebar-overlay {
  display: none;
}

@media (max-width: 768px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    transform: translateX(-100%);
    transition: transform 0.28s;
    width: 200px !important;
  }

  .sidebar.open {
    transform: translateX(0);
  }

  .sidebar-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1000;
  }

  .main-content {
    padding: 16px;
  }
}
</style>
